import { useState, useEffect } from 'react';
import { X, Users, ScrollText, Activity, ShieldCheck, Inbox, Eraser } from 'lucide-react';
import { UsersTab } from './manage/UsersTab';
import { AuditLogTab } from './manage/AuditLogTab';
import { TrackerLogTab } from './manage/TrackerLogTab';
import { AccessRequestsTab } from './manage/AccessRequestsTab';
import { PendingRequestsTab } from './manage/PendingRequestsTab';
import { CleanupDataTab } from './manage/CleanupDataTab';
import { canSeeManageTab, visibleManageTabs, type ManageTabId } from '../utils/manageAccess';
interface ManageModalProps {
  accessLevel: AdobeImsAccessLevel;
  user?: AdobeImsSessionUser | null;
  onClose: () => void;
}
const TABS: {
  key: ManageTabId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  key: 'pending-requests',
  label: 'Pending Requests',
  icon: <Inbox size={16} data-id="element-764" data-mp="10ofqe58wgvhc" />
}, {
  key: 'cleanup-data',
  label: 'Cleanup data',
  icon: <Eraser size={16} data-id="element-765" data-mp="wl9of9stlqgo" />
}, {
  key: 'users',
  label: 'Users',
  icon: <Users size={16} data-id="element-766" data-mp="wnvtn2s8ntvq" />
}, {
  key: 'access-requests',
  label: 'Access Requests',
  icon: <ShieldCheck size={16} data-id="element-767" data-mp="1nzd7ruytqkci" />
}, {
  key: 'dashboard-log',
  label: 'Dashboard Log',
  icon: <ScrollText size={16} data-id="element-768" data-mp="1swc32gu84xca" />
}, {
  key: 'tracker-log',
  label: 'Tracker Log',
  icon: <Activity size={16} data-id="element-769" data-mp="1eyneoz0y782h" />
}];
export function ManageModal({
  accessLevel,
  user,
  onClose
}: ManageModalProps) {
  const visibleTabs = visibleManageTabs(accessLevel);
  const [activeTab, setActiveTab] = useState<ManageTabId>(visibleTabs[0] ?? 'pending-requests');
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-4 px-2" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-770" data-mp="m9qa1ypnt9s7">
      <div className="relative bg-white rounded-xl shadow-2xl w-full flex flex-col" style={{
      maxWidth: '98vw',
      height: '95vh'
    }} onClick={e => e.stopPropagation()} data-id="element-771" data-mp="2syig4s0fzfy3">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200" data-id="element-772" data-mp="2j86zc4vntc9s">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-773" data-mp="2f7n7ngtuv0v1">Manage</h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-774" data-mp="3bv9xux0c5n01">
            <X size={20} data-id="element-775" data-mp="32veish3nt7uq" />
          </button>
        </div>

        {/* Body: sidebar + content */}
        <div className="flex flex-1 min-h-0" data-id="element-776" data-mp="2xwtqr4yk9uhd">
          {/* Left sidebar */}
          <nav className="w-48 border-r border-slate-200 bg-slate-50 flex-shrink-0" data-id="element-777" data-mp="2xywetjg5ldjg">
            <div className="py-3" data-id="element-778" data-mp="1reeqhlhbat0p">
              {TABS.filter(tab => canSeeManageTab(accessLevel, tab.key)).map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-779" data-mp="1zch9iqdzlnb">
                  {tab.icon}
                  {tab.label}
                  {tab.key === 'pending-requests' && unassignedCount > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold" data-id="element-780" data-mp="uni1839m96m3">
                      {unassignedCount}
                    </span>}
                </button>)}
            </div>
          </nav>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-781" data-mp="30j2jxugi7a1l">
            {activeTab === 'pending-requests' && canSeeManageTab(accessLevel, 'pending-requests') && <PendingRequestsTab actor={user ? {
            email: user.email ?? '',
            displayName: user.displayName ?? null
          } : null} onUnassignedCount={setUnassignedCount} data-id="element-782" data-mp="2146fos9xhdrg" />}
            {activeTab === 'cleanup-data' && canSeeManageTab(accessLevel, 'cleanup-data') && <CleanupDataTab actor={user ? {
            email: user.email ?? '',
            displayName: user.displayName ?? null
          } : null} data-id="element-783" data-mp="1mgv1sjq9xho6" />}
            {activeTab === 'users' && canSeeManageTab(accessLevel, 'users') && <UsersTab data-id="element-784" data-mp="1w7y75xq8kit9" />}
            {activeTab === 'access-requests' && canSeeManageTab(accessLevel, 'access-requests') && <AccessRequestsTab data-id="element-785" data-mp="13pkfkfvhtt2m" />}
            {activeTab === 'dashboard-log' && canSeeManageTab(accessLevel, 'dashboard-log') && <AuditLogTab data-id="element-786" data-mp="yr92lw9wj2g6" />}
            {activeTab === 'tracker-log' && canSeeManageTab(accessLevel, 'tracker-log') && <TrackerLogTab data-id="element-787" data-mp="3adugto5jscoz" />}
          </div>
        </div>
      </div>
    </div>;
}